import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Clock, Users, Target, Heart, Mail, MapPin } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">About Hines Time</h1>
              <p className="text-blue-100 mt-1">Our Story and Mission</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">About HinesTime</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              HinesTime is a modern timekeeping and job management application built for service companies and growing teams. 
              Designed by <strong>Scalevisory</strong>, HinesTime makes it simple to track time, manage jobs, coordinate field staff, 
              and keep projects on schedule — all in one clear, easy-to-use system.
            </p>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">HinesTime Helps Businesses:</h3>
            <ul className="space-y-3 text-lg text-gray-700 mb-6">
              <li className="flex items-start">
                <Clock className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span>Track hours accurately and in real-time</span>
              </li>
              <li className="flex items-start">
                <Users className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <span>Assign jobs and monitor progress across teams</span>
              </li>
              <li className="flex items-start">
                <Target className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <span>Improve accountability and communication</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-6 h-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                <span>Simplify payroll and job costing</span>
              </li>
              <li className="flex items-start">
                <Heart className="w-6 h-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                <span>Eliminate paperwork, spreadsheets, and manual time sheets</span>
              </li>
            </ul>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Built for teams who work in the real world — not just behind a desk — HinesTime is intuitive, 
              mobile-friendly, and ready for the field. Whether you have 5 employees or 500, the platform scales 
              as your business grows, with straightforward per-user, per-month pricing.
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border-2 border-blue-200">
              <p className="text-lg text-gray-800 leading-relaxed">
                <strong>HinesTime is part of Scalevisory's mission</strong> to deliver practical tools that make 
                service companies more efficient, agile, and competitive.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <Target className="w-12 h-12 mx-auto text-blue-600 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Our Mission</h3>
              <p className="text-sm text-gray-600">
                Deliver practical tools that make service companies more efficient, agile, and competitive in today's fast-paced marketplace.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 mx-auto text-green-600 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Built by Scalevisory</h3>
              <p className="text-sm text-gray-600">
                Created by industry experts with decades of experience in workforce management, field operations, and software development.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <Heart className="w-12 h-12 mx-auto text-red-600 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Our Values</h3>
              <p className="text-sm text-gray-600">
                Simplicity, reliability, and transparency. We build tools that work in the real world, not just in theory.
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Why HinesTime?</h2>
            <div className="space-y-4 text-gray-700">
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Save Time:</strong>
                  <p className="text-gray-600">Automate timesheets and eliminate manual data entry. What used to take hours now takes minutes.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Target className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Reduce Errors:</strong>
                  <p className="text-gray-600">GPS verification and automated calculations prevent payroll mistakes that cost businesses thousands annually.</p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Ensure Compliance:</strong>
                  <p className="text-gray-600">Toolbox meetings, location tracking, and audit trails keep you compliant with safety and labor regulations.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Users className="w-5 h-5 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Scale Easily:</strong>
                  <p className="text-gray-600">From 5 employees to 500+, HinesTime grows with your business. Add or remove users anytime with simple per-user pricing.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Heart className="w-5 h-5 text-red-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Built for Field Teams:</strong>
                  <p className="text-gray-600">Mobile-friendly, GPS-enabled, and designed for workers in the real world — not just behind a desk.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
          >
            Start Your Free Trial
          </Button>
          <p className="text-sm text-gray-600 mt-3">
            Part of the Scalevisory family of business solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
